// web/src/routes/Kids.tsx
// List of children on the parent's account. Each kid is a card with their
// KidAvatar (kit-colour ring + squad number) on the left and meta on the
// right. Tapping the card navigates into KidDetail.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useKidApi } from '../api/kid';
import type { Kid } from '../types';
import { Button } from '../components/Button';
import { Hero } from '../components/Hero';
import { Section } from '../components/Container';
import { KidAvatar } from '../components/KidAvatar';
import { EmptyState } from '../components/EmptyState';
import { ageFromDob } from '../lib/age';

export function Kids() {
  const api = useKidApi();
  const [kids, setKids] = useState<Kid[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // kidId → signed photo URL. Populated after the list lands so each
  // avatar can render the actual profile photo. Bounded by the per-plan
  // kid cap (1 / 5 / 5).
  const [kidPhotoUrls, setKidPhotoUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.list();
        if (!cancelled) setKids(Array.isArray(res) ? res : []);
      } catch (e) {
        if (!cancelled) setError((e as Error).message || 'Could not load kids.');
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  // Lazy-fetch every kid's photo URL in parallel once the list lands.
  useEffect(() => {
    if (!kids || kids.length === 0) return;
    let cancelled = false;
    (async () => {
      const withPhotos = kids.filter((k) => !!k.photoPath);
      if (withPhotos.length === 0) return;
      const entries = await Promise.all(
        withPhotos.map(async (k) => {
          try {
            const r = await api.photoUrl(k.kidId);
            return [k.kidId, r.url] as const;
          } catch { return null; }
        })
      );
      if (cancelled) return;
      const map: Record<string, string> = {};
      for (const e of entries) if (e) map[e[0]] = e[1];
      setKidPhotoUrls(map);
    })();
    return () => { cancelled = true; };
  }, [kids, api]);

  if (error) {
    return (
      <div>
        <h1>Kids</h1>
        <p style={{ color: 'var(--electric-red)' }}>{error}</p>
      </div>
    );
  }
  if (kids === null) {
    return <p style={{ color: 'var(--muted)' }}>Loading…</p>;
  }

  return (
    <div>
      <Hero
        kicker="Your players"
        title="Kids"
        actions={
          <Link to="/kids/new" style={{ textDecoration: 'none' }}>
            <Button label="+ Add a child" />
          </Link>
        }
      />

      {kids.length === 0 ? (
        <EmptyState
          emoji="⚽"
          title="Start with the player"
          body="Add your first child and we'll set up their journal."
          cta={
            <Link to="/kids/new" style={{ textDecoration: 'none' }}>
              <Button label="Add a child" />
            </Link>
          }
        />
      ) : (
        <Section spacing="lg">
          <div style={{ display: 'grid', gap: 12 }}>
            {kids.map((kid) => {
              const age = ageFromDob(kid.dob);
              const fullName = kid.surname ? `${kid.name} ${kid.surname}` : kid.name;
              return (
                <Link
                  key={kid.kidId}
                  to={`/kids/${encodeURIComponent(kid.kidId)}`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <article
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-base)',
                      padding: 'var(--space-base)',
                      background: 'var(--surface-1)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      boxShadow: 'var(--shadow-1)',
                    }}
                  >
                    <KidAvatar
                      name={kid.name}
                      photoUrl={kidPhotoUrls[kid.kidId]}
                      kitColour={kid.kitColour}
                      squadNumber={kid.squadNumber}
                      size="md"
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h2
                        style={{
                          fontFamily: 'var(--font-display)',
                          fontSize: 'var(--title)',
                          margin: 0,
                          color: 'var(--green)',
                          fontWeight: 500,
                          lineHeight: 1.1,
                        }}
                      >
                        {fullName}
                      </h2>
                      <p
                        style={{
                          margin: '4px 0 0',
                          color: 'var(--muted)',
                          fontSize: 'var(--small)',
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {age !== null ? `Age ${age}` : kid.dob}
                        {/* Position dropped from kid profile 2026-05-19. */}
                        {kid.defaultTeamName ? ` · ${kid.defaultTeamName}` : ''}
                      </p>
                    </div>
                    <span style={{ color: 'var(--muted)', fontSize: 'var(--lede)' }}>›</span>
                  </article>
                </Link>
              );
            })}
          </div>
        </Section>
      )}
    </div>
  );
}
